import { createFlow, Flow, memoizeFlowFactory } from '@adv-frontend/modules-flow';
import { shallowEqual } from 'react-redux';
import type { AnySelector, DropFirstParameter, GetStoreFn } from '../types';
import { createListenersCollection } from '../createListenersCollection';
import { BoundSelectorAndHook, getBoundSelectorAndHook } from './getBoundSelectorAndHook';
import { triggerSelectorCall } from './trackSelectorCall';
import { subscribeToSelector } from './subscribeToSelector';
import { addCurrentSelectorStore } from './createSubscribableSelector';

export interface CreateSelectorFlowOptions<Selector extends AnySelector> {
    /**
     * Функция для получения селектора
     * Передаем селектор как функцию для того,
     * чтобы была возможность его подменить после инициализации приложения
     */
    getSelector: () => Selector;
    /**
     * Функция для получения стора
     */
    getStore: GetStoreFn;
}

export type SelectorFlowFactory<Selector extends AnySelector> = ((
    ...selectorArgs: Parameters<DropFirstParameter<Selector>>
) => Flow<ReturnType<Selector>>) &
    BoundSelectorAndHook<Selector>;

export function createSelectorFlowFactory<Selector extends AnySelector>(
    options: CreateSelectorFlowOptions<Selector>
): SelectorFlowFactory<Selector> {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Добавялем типы для tuple
    const factory = getSelectorFlowFactory(options) as unknown as SelectorFlowFactory<Selector>;
    const [hook, getter] = getBoundSelectorAndHook<Selector>(options);

    Object.defineProperty(factory, Symbol.iterator, {
        value: function* () {
            yield hook;
            yield getter;
        },
        enumerable: false,
    });

    factory[0] = hook;
    factory[1] = getter;

    return factory;
}

function getSelectorFlowFactory<Selector extends AnySelector>({
    getStore,
    getSelector,
}: CreateSelectorFlowOptions<Selector>) {
    type SelectorResult = ReturnType<Selector>;
    type SelectorArgsWithoutState = Parameters<DropFirstParameter<Selector>>;

    const flowFactory = memoizeFlowFactory(
        (selectorArgs: SelectorArgsWithoutState) => {
            return createSelectorFlow({
                getStore,
                getSelector,
                selectorArgs,
            });
        },
        {
            equals(a, b) {
                return shallowEqual(a, b);
            },
        }
    );

    return (...selectorArgs: SelectorArgsWithoutState): Flow<SelectorResult> => {
        return flowFactory(selectorArgs);
    };
}

function createSelectorFlow<Selector extends AnySelector>({
    getStore,
    getSelector,
    selectorArgs,
}: CreateSelectorFlowOptions<Selector> & {
    selectorArgs: Parameters<DropFirstParameter<Selector>>;
}): Flow<ReturnType<Selector>> {
    type SelectorState = Parameters<Selector>[0];
    type SelectorResult = ReturnType<Selector>;

    const getState = () => {
        const store = getStore();
        const selector = getSelector();

        /**
         * Сообщает о вызове модульного селектора, для проверки использования во внешних сторах
         */
        triggerSelectorCall();

        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Стор типизирован через any
        return selector(store.getState() as SelectorState, ...selectorArgs) as SelectorResult;
    };

    const flow = createFlow(getState());

    const listeners = createListenersCollection({
        onAdd(listener) {
            return flow.subscribe(listener);
        },
        onActivate() {
            return subscribeToSelector(
                () => {
                    const store = getStore();
                    addCurrentSelectorStore(store);
                    return getState();
                },
                value => {
                    try {
                        flow.emit(value);
                    } catch (err) {
                        // Метод emit() проксирует ошибки от слушателей потока.
                        // Не хотим, чтобы ошибки слушателей потока влияли на работу самого потока,
                        // поэтому ловим ошибки и просто их логируем
                        console.error(err);
                    }
                }
            );
        },
    });

    return {
        subscribe(listener) {
            return listeners.add(listener);
        },
        getSnapshot() {
            if (!listeners.hasListeners()) {
                return getState();
            }

            return flow.getSnapshot();
        },
    };
}
